"use client"

import type React from "react"
import { useState, useMemo } from "react"
import { cn } from "@/lib/utils"
import {
  ChevronDown,
  ChevronRight,
  Search,
  Brain,
  Sparkles,
  CheckCircle2,
  ExternalLink,
  Copy,
  Check,
  Database,
  Lightbulb,
  AlertTriangle,
  ShieldCheck,
  Shield,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { CitationsPanel, type Citation } from "./citations-panel"
import { TransparencyPanel } from "./transparency-panel"
import { ErrorBoundary as ReactErrorBoundary } from "react-error-boundary"
import { MarkdownRenderer as CustomMarkdownRenderer } from "@/components/chat/markdown-renderer"

// Types for rich message content
import type { ThinkingStep, ThinkingStepConfig, ReasoningData, MemoryUsed, MessageType } from "@/lib/types/reasoning"
import { THINKING_STEPS_CONFIG, hasReasoningContent, normalizeReasoning } from "@/lib/ai/reasoning"
import { logReasoningPanelOpened } from "@/lib/analytics/analytics"
import { normalizeMessageContent } from "@/lib/chat/normalize-message-content"

export type { ThinkingStep, ThinkingStepConfig, ReasoningData, MemoryUsed }
export { THINKING_STEPS_CONFIG }

export interface SearchResult {
  title: string
  url?: string
  snippet?: string
}

export interface InChatCard {
  type: "insight" | "action" | "summary" | "memory"
  title: string
  content: string
  metadata?: Record<string, string>
  actions?: { label: string; onClick?: () => void }[]
}

export interface RichMessageContent {
  text: string
  searchResults?: SearchResult[]
  citations?: Citation[]
  cards?: InChatCard[]
  thinking?: ThinkingStep[]
  reasoning?: ReasoningData
  error?: { code: string; requestId?: string }
  memoryKeys?: string[]
}

// Search Results Component
function SearchResults({ searches }: { searches: SearchResult[] }) {
  const [isExpanded, setIsExpanded] = useState(false)

  if (searches.length === 0) return null

  return (
    <div className="mb-3">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        <Search className="h-3.5 w-3.5 text-blue-400" />
        <span className="font-medium">Searched {searches.length} sources</span>
      </button>

      {isExpanded && (
        <div className="mt-2 ml-5 space-y-2">
          {searches.map((search, i) => (
            <div key={i} className="flex items-start gap-2 text-xs p-2 rounded-lg bg-card/50">
              <ExternalLink className="h-3 w-3 text-muted-foreground mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <a
                  href={search.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline font-medium block truncate"
                >
                  {search.title}
                </a>
                {search.snippet && <p className="text-muted-foreground/70 line-clamp-2 mt-0.5">{search.snippet}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// In-Chat Card Component
function ChatCard({ card }: { card: InChatCard }) {
  const iconMap = {
    insight: <Sparkles className="h-4 w-4 text-amber-400" />,
    action: <CheckCircle2 className="h-4 w-4 text-green-400" />,
    summary: <Brain className="h-4 w-4 text-primary" />,
    memory: <Brain className="h-4 w-4 text-purple-400" />,
  }

  const bgMap = {
    insight: "bg-amber-500/5",
    action: "bg-green-500/5",
    summary: "bg-primary/5",
    memory: "bg-purple-500/5",
  }

  return (
    <div className={cn("rounded-xl p-4 my-3", bgMap[card.type])}>
      <div className="flex items-start gap-3">
        <div className="shrink-0 mt-0.5">{iconMap[card.type]}</div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-sm text-foreground mb-1">{card.title}</h4>
          <p className="text-sm text-muted-foreground leading-relaxed">{card.content}</p>
          {card.metadata && (
            <div className="flex flex-wrap gap-2 mt-2">
              {Object.entries(card.metadata).map(([key, value]) => (
                <span key={key} className="inline-flex items-center gap-1 text-xs bg-card rounded-md px-2 py-0.5">
                  <span className="text-muted-foreground">{key}:</span>
                  <span className="ml-1 font-medium">{value}</span>
                </span>
              ))}
            </div>
          )}
          {card.actions && card.actions.length > 0 && (
            <div className="flex gap-2 mt-3">
              {card.actions.map((action, i) => (
                <Button
                  key={i}
                  variant="outline"
                  size="sm"
                  onClick={action.onClick}
                  className="text-xs h-7 bg-transparent"
                >
                  {action.label}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Code Block with Copy Button
function CodeBlock({ code, language }: { code: string; language?: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="relative group my-3">
      {language && (
        <div className="absolute top-0 left-0 px-2 py-1 text-[10px] font-mono text-muted-foreground bg-[#161b22] rounded-tl-lg rounded-br-lg">
          {language}
        </div>
      )}
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 p-1.5 rounded-md bg-[#161b22] opacity-0 group-hover:opacity-100 transition-opacity"
        title="Copy code"
      >
        {copied ? (
          <Check className="h-3.5 w-3.5 text-green-400" />
        ) : (
          <Copy className="h-3.5 w-3.5 text-muted-foreground" />
        )}
      </button>
      <pre className={cn("overflow-x-auto rounded-lg bg-[#0d1117] p-4 text-sm", language && "pt-8")}>
        <code className="text-gray-300 font-mono text-xs">{code}</code>
      </pre>
    </div>
  )
}

// Custom Markdown Parser (no external dependencies)
function parseMarkdown(text: string): React.ReactNode[] {
  const elements: React.ReactNode[] = []
  const lines = text.split("\n")
  let i = 0
  let listItems: { type: "ul" | "ol"; items: React.ReactNode[] } | null = null
  let codeBlock: { language: string; code: string[] } | null = null

  const parseInline = (text: string): React.ReactNode => {
    // Process inline elements: bold, italic, code, links, citations
    const parts: React.ReactNode[] = []
    let remaining = text
    let key = 0

    while (remaining.length > 0) {
      const citationMatch = remaining.match(/^\[(\d+)\]/)
      if (citationMatch) {
        const citationId = Number.parseInt(citationMatch[1], 10)
        parts.push(
          <span
            key={key++}
            className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-primary/20 text-primary text-[9px] font-medium mx-0.5 align-super cursor-help"
            title={`Source ${citationId}`}
          >
            {citationId}
          </span>,
        )
        remaining = remaining.slice(citationMatch[0].length)
        continue
      }

      // Bold: **text** or __text__
      const boldMatch = remaining.match(/^(\*\*|__)(.+?)\1/)
      if (boldMatch) {
        parts.push(
          <strong key={key++} className="font-semibold text-foreground">
            {parseInline(boldMatch[2])}
          </strong>,
        )
        remaining = remaining.slice(boldMatch[0].length)
        continue
      }

      // Italic: *text* or _text_
      const italicMatch = remaining.match(/^(\*|_)([^*_]+?)\1/)
      if (italicMatch) {
        parts.push(
          <em key={key++} className="italic text-foreground/80">
            {italicMatch[2]}
          </em>,
        )
        remaining = remaining.slice(italicMatch[0].length)
        continue
      }

      // Inline code: `code`
      const codeMatch = remaining.match(/^`([^`]+)`/)
      if (codeMatch) {
        parts.push(
          <code key={key++} className="px-1.5 py-0.5 rounded bg-[#1e293b] text-primary text-xs font-mono">
            {codeMatch[1]}
          </code>,
        )
        remaining = remaining.slice(codeMatch[0].length)
        continue
      }

      // Links: [text](url)
      const linkMatch = remaining.match(/^\[([^\]]+)\]$$([^)]+)$$/)
      if (linkMatch) {
        parts.push(
          <a
            key={key++}
            href={linkMatch[2]}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:text-primary/80 underline underline-offset-2 inline-flex items-center gap-1"
          >
            {linkMatch[1]}
            <ExternalLink className="h-3 w-3" strokeWidth={1.5} />
          </a>,
        )
        remaining = remaining.slice(linkMatch[0].length)
        continue
      }

      // Regular text - take one character at a time until we find a special char
      const nextSpecial = remaining.search(/[*_`[]/)
      if (nextSpecial === -1) {
        parts.push(remaining)
        break
      } else if (nextSpecial === 0) {
        // Special char that didn't match a pattern, treat as regular text
        parts.push(remaining[0])
        remaining = remaining.slice(1)
      } else {
        parts.push(remaining.slice(0, nextSpecial))
        remaining = remaining.slice(nextSpecial)
      }
    }

    return parts.length === 1 ? parts[0] : <>{parts}</>
  }

  const flushList = () => {
    if (listItems) {
      if (listItems.type === "ul") {
        elements.push(
          <ul key={elements.length} className="list-disc list-outside ml-4 space-y-1 mb-3 text-sm text-foreground/90">
            {listItems.items}
          </ul>,
        )
      } else {
        elements.push(
          <ol
            key={elements.length}
            className="list-decimal list-outside ml-4 space-y-1 mb-3 text-sm text-foreground/90"
          >
            {listItems.items}
          </ol>,
        )
      }
      listItems = null
    }
  }

  while (i < lines.length) {
    const line = lines[i]

    // Code block start/end
    if (line.startsWith("```")) {
      if (codeBlock) {
        // End code block
        elements.push(
          <CodeBlock key={elements.length} code={codeBlock.code.join("\n")} language={codeBlock.language} />,
        )
        codeBlock = null
      } else {
        // Start code block
        flushList()
        const language = line.slice(3).trim()
        codeBlock = { language, code: [] }
      }
      i++
      continue
    }

    // Inside code block
    if (codeBlock) {
      codeBlock.code.push(line)
      i++
      continue
    }

    // Empty line
    if (line.trim() === "") {
      flushList()
      i++
      continue
    }

    // Headings
    const h1Match = line.match(/^# (.+)$/)
    if (h1Match) {
      flushList()
      elements.push(
        <h1 key={elements.length} className="text-xl font-bold text-foreground mt-6 mb-3 first:mt-0">
          {parseInline(h1Match[1])}
        </h1>,
      )
      i++
      continue
    }

    const h2Match = line.match(/^## (.+)$/)
    if (h2Match) {
      flushList()
      elements.push(
        <h2 key={elements.length} className="text-lg font-semibold text-foreground mt-8 mb-2.5">
          {parseInline(h2Match[1])}
        </h2>,
      )
      i++
      continue
    }

    const h3Match = line.match(/^### (.+)$/)
    if (h3Match) {
      flushList()
      elements.push(
        <h3 key={elements.length} className="text-base font-semibold text-foreground mt-6 mb-2">
          {parseInline(h3Match[1])}
        </h3>,
      )
      i++
      continue
    }

    const h4Match = line.match(/^#### (.+)$/)
    if (h4Match) {
      flushList()
      elements.push(
        <h4 key={elements.length} className="text-sm font-semibold text-foreground mt-3 mb-1">
          {parseInline(h4Match[1])}
        </h4>,
      )
      i++
      continue
    }

    // Blockquote
    const blockquoteMatch = line.match(/^> (.+)$/)
    if (blockquoteMatch) {
      flushList()
      elements.push(
        <blockquote
          key={elements.length}
          className="border-l-2 border-primary/50 pl-4 my-3 text-sm text-muted-foreground italic"
        >
          {parseInline(blockquoteMatch[1])}
        </blockquote>,
      )
      i++
      continue
    }

    // Horizontal rule
    if (line.match(/^(-{3,}|\*{3,}|_{3,})$/)) {
      flushList()
      elements.push(<hr key={elements.length} className="border-border/50 my-4" />)
      i++
      continue
    }

    // Unordered list item
    const ulMatch = line.match(/^[-*+] (.+)$/)
    if (ulMatch) {
      flushList()
      listItems = { type: "ul", items: [] }
      listItems.items.push(
        <li key={listItems.items.length} className="leading-relaxed pl-1">
          {parseInline(ulMatch[1])}
        </li>,
      )
      i++
      continue
    }

    // Ordered list item
    const olMatch = line.match(/^\d+\. (.+)$/)
    if (olMatch) {
      if (!listItems || listItems.type !== "ol") {
        flushList()
        listItems = { type: "ol", items: [] }
      }
      listItems.items.push(
        <li key={listItems.items.length} className="leading-relaxed pl-1">
          {parseInline(olMatch[1])}
        </li>,
      )
      i++
      continue
    }

    // Checklist item support (- [ ] or - [x])
    const checklistMatch = line.match(/^[-*] \[([ xX])\] (.+)$/)
    if (checklistMatch) {
      flushList()
      const isChecked = checklistMatch[1].toLowerCase() === "x"
      elements.push(
        <ul key={elements.length} className="space-y-1 mb-3">
          <ChecklistItem checked={isChecked}>{parseInline(checklistMatch[2])}</ChecklistItem>
        </ul>,
      )
      i++
      continue
    }

    // Regular paragraph
    flushList()
    elements.push(
      <p key={elements.length} className="text-sm text-foreground/90 leading-relaxed mb-3 last:mb-0">
        {parseInline(line)}
      </p>,
    )
    i++
  }

  // Flush any remaining list
  flushList()

  // Close any unclosed code block
  if (codeBlock) {
    elements.push(<CodeBlock key={elements.length} code={codeBlock.code.join("\n")} language={codeBlock.language} />)
  }

  return elements
}

// Reasoning Panel Component
function ReasoningPanel({
  reasoning,
  messageId,
  messageType = "general",
}: {
  reasoning?: ReasoningData
  messageId?: string
  messageType?: MessageType
}) {
  const [isExpanded, setIsExpanded] = useState(false)

  const normalizedReasoning = normalizeReasoning(reasoning)
  const hasContent = hasReasoningContent(normalizedReasoning)

  const handleExpand = () => {
    const newExpanded = !isExpanded
    setIsExpanded(newExpanded)

    if (newExpanded && messageId) {
      logReasoningPanelOpened(messageId, messageType, reasoning)
    }
  }

  return (
    <div className="mt-3 pt-3 border-t border-border/30">
      <button
        onClick={handleExpand}
        className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors group"
      >
        {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        <Lightbulb className="h-3.5 w-3.5 text-amber-400/70" />
        <span className="font-medium">See how I decided this</span>
      </button>

      {isExpanded && (
        <div className="mt-3 space-y-4 pl-5">
          {hasContent ? (
            <>
              {/* Memories I Used */}
              {normalizedReasoning?.memoriesUsed && normalizedReasoning.memoriesUsed.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                    <Database className="h-3 w-3" />
                    Memories I used
                  </h4>
                  <div className="flex flex-wrap gap-1.5">
                    {normalizedReasoning.memoriesUsed.map((memory, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-[var(--surface-elevated)] text-foreground/80"
                        title={memory.value || `${memory.area}: ${memory.topic}`}
                      >
                        <span className="text-[var(--brain)]">{memory.area}</span>
                        <span className="text-muted-foreground/50">·</span>
                        <span>{memory.topic}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Key Factors */}
              {normalizedReasoning?.keyFactors && normalizedReasoning.keyFactors.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                    <Brain className="h-3 w-3" />
                    What I considered
                  </h4>
                  <ul className="space-y-1.5">
                    {normalizedReasoning.keyFactors.map((factor, i) => (
                      <li key={i} className="text-xs text-foreground/80 flex items-start gap-2">
                        <span className="text-[var(--brain)] mt-0.5">•</span>
                        <span>{factor}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Assumptions */}
              {normalizedReasoning?.assumptions && normalizedReasoning.assumptions.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                    <AlertTriangle className="h-3 w-3" />
                    Assumptions / unknowns
                  </h4>
                  <ul className="space-y-1.5">
                    {normalizedReasoning.assumptions.map((assumption, i) => (
                      <li key={i} className="text-xs text-muted-foreground flex items-start gap-2">
                        <span className="text-amber-400/70 mt-0.5">?</span>
                        <span>{assumption}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Safety Check */}
              {normalizedReasoning?.safetyCheck && (
                <div className="space-y-2">
                  <h4 className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                    <ShieldCheck className="h-3 w-3" />
                    Truth & safety check
                  </h4>
                  <div
                    className={cn(
                      "text-xs px-2 py-1.5 rounded-md inline-flex items-center gap-1.5",
                      normalizedReasoning.safetyCheck.status === "passed" && "bg-emerald-500/10 text-emerald-400",
                      normalizedReasoning.safetyCheck.status === "warning" && "bg-amber-500/10 text-amber-400",
                      normalizedReasoning.safetyCheck.status === "blocked" && "bg-red-500/10 text-red-400",
                    )}
                  >
                    {normalizedReasoning.safetyCheck.status === "passed" && <CheckCircle2 className="h-3 w-3" />}
                    {normalizedReasoning.safetyCheck.status === "warning" && <AlertTriangle className="h-3 w-3" />}
                    {normalizedReasoning.safetyCheck.status === "blocked" && <Shield className="h-3 w-3" />}
                    <span className="capitalize">{normalizedReasoning.safetyCheck.status}</span>
                    {normalizedReasoning.safetyCheck.message && (
                      <span className="text-muted-foreground">— {normalizedReasoning.safetyCheck.message}</span>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="space-y-3">
              <div className="text-xs text-muted-foreground">
                <p className="mb-2">This response was generated based on:</p>
                <ul className="space-y-1.5">
                  <li className="flex items-start gap-2">
                    <span className="text-[var(--brain)] mt-0.5">•</span>
                    <span>Your conversation context</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[var(--brain)] mt-0.5">•</span>
                    <span>BizzIQ's business knowledge base</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[var(--brain)] mt-0.5">•</span>
                    <span>Any memories you've saved so far</span>
                  </li>
                </ul>
              </div>
              <div className="text-xs text-muted-foreground/70 italic">
                Detailed reasoning will appear for advisory questions once you've built more of your business profile.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Helper to strip :::card blocks from text before rendering
function stripCardBlocks(text: string): string {
  // Remove :::card blocks (the cards are rendered separately via richContent.cards)
  return text
    .replace(/:::card\s*\n[\s\S]*?\n:::/g, "") // Multi-line card blocks
    .replace(/:::card[\s\S]*?:::/g, "") // Single-line card blocks
    .replace(/\{"sectionId":[^}]+\}/g, "") // Raw JSON card data
    .replace(/\n{3,}/g, "\n\n") // Clean up excessive newlines
    .trim()
}

// ChecklistItem component for interactive checkboxes
function ChecklistItem({
  checked,
  children,
  onToggle,
}: {
  checked: boolean
  children: React.ReactNode
  onToggle?: () => void
}) {
  const [isChecked, setIsChecked] = useState(checked)

  const handleToggle = () => {
    setIsChecked(!isChecked)
    onToggle?.()
  }

  return (
    <li className="flex items-start gap-2 leading-relaxed pl-1">
      <button
        onClick={handleToggle}
        className={cn(
          "mt-0.5 h-4 w-4 rounded border flex-shrink-0 flex items-center justify-center transition-colors",
          isChecked
            ? "bg-[var(--brain)] border-[var(--brain)] text-white"
            : "border-muted-foreground/50 hover:border-[var(--brain)]",
        )}
      >
        {isChecked && <Check className="h-3 w-3" />}
      </button>
      <span className={cn(isChecked && "line-through text-muted-foreground")}>{children}</span>
    </li>
  )
}

interface MessageRendererProps {
  content: string
  messageId: string
  messageType: MessageType
  requestId?: string
  isStreaming?: boolean
  variant?: "default" | "user-card" | "advisor-document"
}

// Error Fallback Component
function ErrorFallback({
  error,
  resetErrorBoundary,
  requestId,
  messageId,
  contentType,
}: {
  error: Error
  resetErrorBoundary: () => void
  requestId?: string
  messageId?: string
  contentType?: string
}) {
  console.error("[v0][MessageRenderer] React render error caught:", {
    error: error.message,
    stack: error.stack,
    requestId,
    messageId,
    contentType,
  })

  return (
    <div className="p-4 border border-destructive/50 rounded-lg bg-destructive/10">
      <p className="text-sm font-medium text-destructive mb-2">Message Rendering Error</p>
      <p className="text-xs text-muted-foreground mb-3">
        {error.message || "An unexpected error occurred while rendering this message."}
      </p>
      {requestId && <p className="text-xs text-muted-foreground">Request ID: {requestId}</p>}
      <button onClick={resetErrorBoundary} className="mt-2 text-xs text-[var(--cta)] hover:underline">
        Try again
      </button>
    </div>
  )
}

// Main Message Renderer
export function MessageRenderer({
  content,
  messageId,
  messageType,
  requestId,
  isStreaming = false,
  variant = "default",
}: MessageRendererProps) {
  console.log("[v0][MessageRenderer] Rendering message:", {
    messageId,
    contentType: typeof content,
    hasContent: !!content,
    isString: typeof content === "string",
    contentKeys: typeof content === "object" && content ? Object.keys(content) : undefined,
    variant,
  })

  const normalized = normalizeMessageContent(content)

  // Convert normalized content to RichMessageContent format
  let richContent: RichMessageContent

  if (!normalized.hasText && normalized.searchResults.length === 0 && normalized.cards.length === 0) {
    console.warn("[v0][MessageRenderer] Normalized content is empty", {
      messageId,
      requestId,
    })
    richContent = { text: "" }
  } else {
    richContent = {
      text: normalized.text,
      searchResults: normalized.searchResults as SearchResult[],
      citations: normalized.citations as Citation[],
      cards: normalized.cards as InChatCard[],
      thinking: normalized.thinking as ThinkingStep[],
      reasoning: undefined, // Will be derived if needed
      error: undefined,
      memoryKeys: normalized.memoryKeys as string[],
    }
  }

  const containerClassName = useMemo(() => {
    if (variant === "advisor-document") {
      return "advisor-prose"
    } else if (variant === "user-card") {
      return "font-sans text-[18px] leading-[1.55] font-normal"
    }
    return ""
  }, [variant])

  return (
    <ReactErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={(error, info) => {
        console.error("[v0][MessageRenderer] Error boundary caught error:", error, info)
      }}
    >
      <div className={cn("message-renderer-root", containerClassName)}>
        {/* Search Results */}
        {richContent.searchResults && richContent.searchResults.length > 0 && (
          <SearchResults searches={richContent.searchResults} />
        )}

        {/* Main Text Content with Markdown */}
        <CustomMarkdownRenderer
          content={richContent.text}
          mode="advisory"
          variant={variant === "user-card" ? "user" : "advisor"}
          className={cn(
            "max-w-none",
            variant === "advisor-document" ? "" : variant === "user-card" ? "" : "prose prose-sm",
          )}
        />

        {/* Transparency Panel */}
        {richContent.reasoning?.decisionSnapshot && (
          <TransparencyPanel
            transparency={richContent.reasoning.decisionSnapshot}
            messageId={messageId}
            messageType={messageType}
          />
        )}

        {/* Legacy reasoning panel for backwards compatibility */}
        {richContent.reasoning && !richContent.reasoning.decisionSnapshot && (
          <ReasoningPanel reasoning={richContent.reasoning} messageId={messageId} messageType={messageType} />
        )}

        {/* In-Chat Cards */}
        {richContent.cards && richContent.cards.map((card, i) => <ChatCard key={i} card={card} />)}

        {richContent.citations && richContent.citations.length > 0 && (
          <CitationsPanel citations={richContent.citations} highlightedCitationId={null} onCitationClick={() => {}} />
        )}

        {richContent.memoryKeys && richContent.memoryKeys.length > 0 && (
          <div className="mt-3 pt-3 border-t border-border/50">
            <div className="flex items-start gap-2 text-xs text-muted-foreground">
              <svg className="h-3.5 w-3.5 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <span className="font-medium">Used {richContent.memoryKeys.length} saved memories</span>
                <span className="text-muted-foreground/60 ml-1">from your Business Brain</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </ReactErrorBoundary>
  )
}

function getBaseTextClasses(variant: "default" | "user-card" | "advisor-document"): string {
  if (variant === "advisor-document") {
    return "font-display text-[20px] leading-[1.65] text-[var(--foreground)]"
  } else if (variant === "user-card") {
    return "font-sans text-[18px] leading-[1.55] font-normal"
  }
  return ""
}

function getProseVariantClasses(variant: "default" | "user-card" | "advisor-document"): string {
  if (variant === "advisor-document") {
    return cn(
      // Advisor document prose styles with exact locked sizes
      "prose-headings:font-display",
      "prose-h1:hidden", // H1 suppressed - not used in chat
      // H2: 30px, weight 600, line-height 38px (1.27), mt-24px, mb-12px
      "prose-h2:text-[30px] prose-h2:font-display prose-h2:font-semibold prose-h2:leading-[38px] prose-h2:mt-6 prose-h2:mb-3",
      // H3: 26px, weight 600, line-height 34px (1.31), mt-20px, mb-10px
      "prose-h3:text-[26px] prose-h3:font-display prose-h3:font-semibold prose-h3:leading-[34px] prose-h3:mt-5 prose-h3:mb-2.5",
      // H4: 22px, weight 600, line-height 30px (1.36), mt-16px, mb-8px
      "prose-h4:text-[22px] prose-h4:font-display prose-h4:font-semibold prose-h4:leading-[30px] prose-h4:mt-4 prose-h4:mb-2",
      // Body: 20px, weight 400, line-height 32px (1.6), paragraph spacing 12px
      "prose-p:text-[20px] prose-p:font-display prose-p:font-normal prose-p:leading-[32px] prose-p:mt-3",
      // Lists: 20px, line-height 32px, item spacing 10px, left padding 28px
      "prose-ul:text-[20px] prose-ul:leading-[32px] prose-ul:mt-3 prose-ul:pl-7",
      "prose-ol:text-[20px] prose-ol:leading-[32px] prose-ol:mt-3 prose-ol:pl-7 prose-ol:list-decimal",
      "prose-li:text-[20px] prose-li:leading-[32px] prose-li:mt-2.5",
      // Neutral markers - remove green bullets and circled numbers
      "prose-ul:marker:text-[var(--muted-foreground)]",
      "prose-ol:marker:text-[var(--muted-foreground)]",
      "prose-strong:font-semibold",
      "prose-em:italic prose-em:text-[var(--muted-foreground)]",
      "prose-blockquote:border-l-2 prose-blockquote:border-[var(--brain)] prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-[var(--muted-foreground)]",
      "prose-hr:my-6 prose-hr:border-[var(--border)]",
      // Table styles
      "prose-table:text-sm prose-table:my-4",
      "prose-thead:font-sans prose-thead:text-xs prose-thead:uppercase prose-thead:text-[var(--muted-foreground)]",
      "prose-tbody:font-display",
      "prose-tr:border-b prose-tr:border-[var(--border)]/30",
      "prose-th:p-2 prose-th:text-left",
      "prose-td:p-2",
    )
  } else if (variant === "user-card") {
    return cn(
      // Zero out paragraph margins
      "prose-p:mt-0 prose-p:mb-0",
    )
  }
  return ""
}
